import React from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';

/**
 * The one copy affordance in the app: a small icon button that flips to a check
 * for two seconds after a successful copy and carries the caller's label.
 *
 * Hoisted out of `RequestDetail` so the starter cURL snippet in the empty
 * inspector behaves exactly like the copy controls in the details pane instead
 * of being a second, subtly different implementation. The caller owns the
 * clipboard write and the `copiedField` state; this only renders it.
 */
export interface CopyButtonProps {
  /** Identifies this button's text within the caller's `copiedField` state. */
  field: string;
  /** The text to place on the clipboard. */
  text: string;
  /** Accessible label, e.g. `Copy cURL command`. */
  label: string;
  /** The field that was copied last, or null. */
  copiedField: string | null;
  /** Called with `text` and `field` when the button is pressed. */
  onCopy: (text: string, field: string) => void;
}

const CopyButton: React.FC<CopyButtonProps> = ({ field, text, label, copiedField, onCopy }) => {
  const copied = copiedField === field;

  return (
    <Button
      type="button"
      variant={copied ? 'success' : 'outline-secondary'}
      size="sm"
      className="copy-button"
      onClick={() => onCopy(text, field)}
      aria-label={copied ? `${label} copied` : label}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} aria-hidden="true" />
    </Button>
  );
};

export default CopyButton;
